import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo, useSyncExternalStore } from 'react';
import { io, Socket } from 'socket.io-client';
import { useUser, useOrganization } from '@clerk/clerk-react';
import { useOrganizationContext } from '@/contexts/OrganizationContext';
import { chatActiveStore } from '@/lib/chatActiveStore';
import { getTotalUnreadCount as fetchTotalUnreadCountRpc, markChannelRead } from '@/lib/chatClient';
import type { ChatAttachment } from '@/types/chat-attachments';
import type { MentionNewEvent } from '@/types/chat-mentions';
import type { UserStatus } from '@/components/chat/types';

// Determine WebSocket URL based on current hostname
function getWebSocketUrl(): string {
    if (import.meta.env.VITE_CHAT_WS_URL) {
        return import.meta.env.VITE_CHAT_WS_URL;
    }

    const hostname = window.location.hostname;

    if (hostname === 'os.openinsurance.ai' || hostname === 'www.openinsurance.ai') {
        return 'https://organization-chat-production.up.railway.app';
    }

    if (hostname === 'qaos.openinsurance.ai') {
        return 'https://organization-chat-qa.up.railway.app';
    }

    return 'http://localhost:3001';
}

const WEBSOCKET_URL = getWebSocketUrl();

// ==================== SOCKET EVENT DATA TYPES ====================

interface UserProfile {
    id: string;
    name: string;
    avatar?: string;
}

interface Message {
    id: string;
    channel_id: string;
    user_profile_id: string;
    content: string;
    attachments?: ChatAttachment[];
    reply_to_message_id?: string;
    thread_parent_id?: string;
    thread_reply_count?: number;
    thread_last_reply_at?: string;
    thread_participant_ids?: string[];
    is_edited?: boolean;
    forwarded_from?: any;
    created_at: string;
    updated_at?: string;
    user: UserProfile;
}

interface ThreadUpdate {
    threadParentId: string;
    replyCount: number;
    lastReplyAt: string;
    participantIds: string[];
}

interface ReactionUpdate {
    messageId: string;
    reactions: { emoji: string; count: number; userProfileIds: string[] }[];
}

interface TypingUser {
    id: string;
    name: string;
    avatar?: string;
}

// Event payload types for socket handlers
interface MessageNotificationData {
    channelId: string;
    message: Message;
    isInChannel?: boolean;
}

interface ChannelDeletedData {
    channelId: string;
}

interface ChannelAvatarUpdatedData {
    channelId: string;
    avatarUrl: string | null;
}

interface UserAvatarUpdatedData {
    userProfileId: string;
    avatarUrl: string | null;
}

interface UserProfileUpdatedData {
    userProfileId: string;
    bannerColor?: string | null;
    bannerUrl?: string | null;
    bio?: string | null;
}

export interface NotificationEvent {
    type: 'message' | 'mention';
    channelId: string;
    senderName: string;
    preview: string;
    senderAvatar?: string;
}

// ==================== INLINE NOTIFICATION HELPERS ====================
// Used when ChatWindow is not mounted (chat closed) — reads prefs from localStorage

const PREFS_KEY = 'chat-preferences';

// Module-level org logo URL — updated by the Provider, read by fireInlineNotification
let _orgLogoUrl: string | null = null;

function readNotificationPrefs() {
    try {
        const stored = localStorage.getItem(PREFS_KEY);
        if (stored) {
            const p = JSON.parse(stored);
            return {
                desktop: p.notifications?.desktop ?? true,
                sound: p.notifications?.sound ?? true,
                mentionsOnly: p.notifications?.mentionsOnly ?? false,
                status: p.status || 'online',
            };
        }
    } catch { /* noop */ }
    return { desktop: true, sound: true, mentionsOnly: false, status: 'online' };
}

function fireInlineNotification(
    senderName: string,
    preview: string,
    type: 'message' | 'mention',
    senderAvatar?: string,
) {
    const prefs = readNotificationPrefs();
    if (prefs.status === 'dnd') return;
    if (prefs.mentionsOnly && type !== 'mention') return;

    const isMention = type === 'mention';

    if (prefs.desktop && 'Notification' in window && Notification.permission === 'granted') {
        const body = isMention
            ? `@${senderName} mentioned you: ${preview}`
            : `${senderName}: ${preview}`;

        const notification = new Notification(senderName, {
            body,
            icon: senderAvatar || _orgLogoUrl || '/favicon.ico',
            badge: _orgLogoUrl || '/favicon.ico',
            // Stable tag per sender — deduplicates mention+message pairs for same sender
            tag: `chat-inline-${senderName}`,
            silent: true,
            requireInteraction: isMention,
        } as NotificationOptions);

        notification.onclick = () => {
            window.focus();
            chatActiveStore.setActive(true);
            notification.close();
        };
    }

    if (prefs.sound) {
        const audio = new Audio('/sounds/notification.mp3');
        audio.volume = 0.5;
        audio.play().catch(() => {});
    }
}

interface ChannelAddedData {
    channelId: string;
}

interface MessageUpdatedData {
    id: string;
    channel_id: string;
    content: string;
    is_edited: boolean;
    updated_at: string;
}

interface MessageDeletedData {
    messageId: string;
    channelId: string;
}

interface TypingActiveData {
    channelId: string;
    user: TypingUser;
}

interface TypingStoppedData {
    channelId: string;
    userId: string;
}

interface ChannelJoinedData {
    channelId: string;
    role: string;
    isMuted: boolean;
    onlineMembers: UserProfile[];
}

interface ChannelUserData {
    channelId: string;
    user: UserProfile;
}

interface ThreadTypingActiveData {
    threadParentId: string;
    user: TypingUser;
}

interface ThreadTypingStoppedData {
    threadParentId: string;
    userId: string;
}

// ==================== SUB-CONTEXT TYPES ====================
// Split into 4 contexts to prevent cascading re-renders:
// - Socket: connection + stable method refs (rarely changes)
// - Messages: message/notification state (changes on every message event)
// - Presence: typing/online state (changes on every typing/presence event)
// - Unread: badge count (changes on count updates)

interface ChatSocketContextType {
    connected: boolean;
    joinChannel: (channelId: string) => void;
    leaveChannel: (channelId: string) => void;
    deleteChannel: (channelId: string) => void;
    notifyMemberAdded: (channelId: string, userProfileId: string) => void;
    sendMessage: (channelId: string, content: string, replyToId?: string, attachments?: ChatAttachment[]) => void;
    editMessage: (messageId: string, content: string) => void;
    deleteMessage: (messageId: string, mode?: 'for_me' | 'for_everyone') => void;
    startTyping: (channelId: string) => void;
    stopTyping: (channelId: string) => void;
    joinThread: (threadParentId: string, channelId: string) => void;
    leaveThread: (threadParentId: string) => void;
    sendThreadMessage: (channelId: string, threadParentId: string, content: string, replyToId?: string, attachments?: ChatAttachment[]) => void;
    startThreadTyping: (threadParentId: string) => void;
    stopThreadTyping: (threadParentId: string) => void;
    toggleReaction: (channelId: string, messageId: string, emoji: string) => void;
    forwardMessage: (targetChannelId: string, originalMessageId: string, content: string, attachments?: any[]) => void;
    notifyChannelAvatarUpdate: (channelId: string, avatarUrl: string | null) => void;
    notifyUserAvatarUpdate: (avatarUrl: string | null) => void;
    notifyUserProfileUpdate: (updates: { bannerColor?: string | null; bannerUrl?: string | null; bio?: string | null }) => void;
    // Clear callbacks are stable refs — placed here so consumers don't need MessagesContext
    clearChannelNotifications: (channelId: string) => void;
    flushChannelLastMessages: () => Record<string, LastMessagePreview>;
    clearChannelsRefresh: () => void;
    clearDeletedChannel: (channelId: string) => void;
    clearThreadMessages: (threadParentId: string) => void;
    clearPendingThreadUpdates: () => void;
    clearPendingMessageDeletions: () => void;
    clearPendingMessageEdits: () => void;
    clearReactionUpdates: () => void;
    clearMentionNotifications: () => void;
    clearPendingChannelUpdates: () => void;
    clearPendingUserAvatarUpdates: () => void;
    clearPendingProfileUpdates: () => void;
    clearConsumedWsMessages: (channelId: string) => void;
    consumeWsMessages: (channelId: string) => Message[];
    clearPendingMessageSendErrors: () => void;
    registerNotificationCallback: (cb: (event: NotificationEvent) => void) => () => void;
    registerMessageDispatch: (cb: (message: Message) => void) => () => void;
}

interface LastMessagePreview {
    content: string;
    sender_name: string;
    created_at: string;
}

interface ChatMessagesContextType {
    messages: Record<string, Message[]>;
    threadMessages: Record<string, Message[]>;
    channelNotifications: Record<string, number>;
    channelsNeedRefresh: boolean;
    deletedChannelIds: Set<string>;
    activeThreadId: string | null;
    pendingThreadUpdates: ThreadUpdate[];
    pendingMessageDeletions: string[];
    pendingMessageEdits: { id: string; content: string }[];
    pendingChannelUpdates: ChannelAvatarUpdatedData[];
    pendingUserAvatarUpdates: UserAvatarUpdatedData[];
    pendingProfileUpdates: UserProfileUpdatedData[];
    pendingMessageSendErrors: number;
    reactionUpdates: ReactionUpdate[];
    mentionNotifications: MentionNewEvent[];
}

interface ChatPresenceContextType {
    typingUsers: Record<string, TypingUser[]>;
    onlineUsers: Record<string, UserProfile[]>;
    threadTypingUsers: Record<string, TypingUser[]>;
    onlineUserIds: Set<string>;
    userStatuses: Map<string, UserStatus>;
    currentUserStatus: UserStatus;
    setUserStatus: (status: UserStatus) => void;
}

interface ChatUnreadContextType {
    totalUnreadCount: number;
    unreadDisplayCount: string;
    refreshUnreadCount: () => Promise<void>;
    adjustTotalUnread: (delta: number) => void;
}

// Combined type for backwards compatibility
interface ChatWebSocketContextType extends ChatSocketContextType, ChatMessagesContextType, ChatPresenceContextType, ChatUnreadContextType {}

const ChatSocketContext = createContext<ChatSocketContextType | null>(null);
const ChatMessagesContext = createContext<ChatMessagesContextType | null>(null);
const ChatPresenceContext = createContext<ChatPresenceContextType | null>(null);
const ChatUnreadContext = createContext<ChatUnreadContextType | null>(null);

export function ChatWebSocketProvider({ children }: { children: React.ReactNode }) {
    const { user } = useUser();
    const { currentOrganization } = useOrganizationContext();
    const { organization: clerkOrg } = useOrganization();
    const socketRef = useRef<Socket | null>(null);

    // Keep module-level org logo URL in sync for inline notifications
    _orgLogoUrl = clerkOrg?.imageUrl || null;

    // Only activate chat when the chat app window exists (open or minimized)
    const isChatAppRunning = useSyncExternalStore(
        chatActiveStore.subscribe,
        chatActiveStore.getSnapshot
    );

    // Ref mirror for use inside socket event closures
    const isChatAppRunningRef = useRef(isChatAppRunning);
    isChatAppRunningRef.current = isChatAppRunning;

    // Connection state
    const [connected, setConnected] = useState(false);

    // Track pending channel join for retry logic
    const pendingJoinRef = useRef<{ channelId: string; timer: NodeJS.Timeout } | null>(null);
    const joinedChannelRef = useRef<string | null>(null);
    const markReadTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Channel state
    const [messages, _setMessages] = useState<Record<string, Message[]>>({});
    const wsMessagesRef = useRef<Record<string, Message[]>>({});
    // Wrapper that keeps ref in sync with state for synchronous reads
    const setMessages: typeof _setMessages = useCallback((action) => {
        _setMessages(prev => {
            const next = typeof action === 'function' ? action(prev) : action;
            wsMessagesRef.current = next;
            return next;
        });
    }, []);
    const [typingUsers, setTypingUsers] = useState<Record<string, TypingUser[]>>({});
    const [onlineUsers, setOnlineUsers] = useState<Record<string, UserProfile[]>>({});
    const [channelNotifications, setChannelNotifications] = useState<Record<string, number>>({});
    const channelLastMessagesRef = useRef<Record<string, LastMessagePreview>>({});
    const [channelsNeedRefresh, setChannelsNeedRefresh] = useState(false);
    const [deletedChannelIds, setDeletedChannelIds] = useState<Set<string>>(new Set());

    // Thread state
    const [threadMessages, setThreadMessages] = useState<Record<string, Message[]>>({});
    const [threadTypingUsers, setThreadTypingUsers] = useState<Record<string, TypingUser[]>>({});
    const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
    const [pendingThreadUpdates, setPendingThreadUpdates] = useState<ThreadUpdate[]>([]);

    // Pending message deletions (for sync to reducer)
    const [pendingMessageDeletions, setPendingMessageDeletions] = useState<string[]>([]);

    // Pending message edits (for sync to reducer)
    const [pendingMessageEdits, setPendingMessageEdits] = useState<{ id: string; content: string }[]>([]);

    // Pending channel updates (avatar changes from other users)
    const [pendingChannelUpdates, setPendingChannelUpdates] = useState<ChannelAvatarUpdatedData[]>([]);

    // Pending user avatar updates (from other users)
    const [pendingUserAvatarUpdates, setPendingUserAvatarUpdates] = useState<UserAvatarUpdatedData[]>([]);

    // Pending user profile updates (banner/bio from other users)
    const [pendingProfileUpdates, setPendingProfileUpdates] = useState<UserProfileUpdatedData[]>([]);

    // Counter for message send errors (triggers optimistic message cleanup)
    const [pendingMessageSendErrors, setPendingMessageSendErrors] = useState(0);

    // Reaction state
    const [reactionUpdates, setReactionUpdates] = useState<ReactionUpdate[]>([]);

    // Mention state
    const [mentionNotifications, setMentionNotifications] = useState<MentionNewEvent[]>([]);

    // Notification callback ref — invoked directly from WS handlers (zero re-renders)
    const notificationCallbackRef = useRef<((event: NotificationEvent) => void) | null>(null);

    // Direct message dispatch callback — bypasses intermediate state + sync effect for faster delivery
    const messageDispatchRef = useRef<((message: Message) => void) | null>(null);

    // Presence state
    const [userStatuses, setUserStatuses] = useState<Map<string, UserStatus>>(new Map());
    const [currentUserStatus, setCurrentUserStatus] = useState<UserStatus>('online');

    // Derive onlineUserIds from userStatuses (all keys except 'invisible') for backward compat
    const onlineUserIds = useMemo(() => {
        const ids = new Set<string>();
        for (const [id, status] of userStatuses) {
            if (status !== 'invisible') {
                ids.add(id);
            }
        }
        return ids;
    }, [userStatuses]);

    // Unread count state (for badge)
    const [totalUnreadCount, setTotalUnreadCount] = useState(0);

    // Fetch unread count via lightweight single RPC call
    const fetchUnreadCount = useCallback(async () => {
        if (!user?.id || !currentOrganization?.id) {
            setTotalUnreadCount(0);
            return;
        }

        try {
            const total = await fetchTotalUnreadCountRpc(currentOrganization.id, user.id);
            setTotalUnreadCount(total);
        } catch {
            // Failed to fetch unread count
        }
    }, [user?.id, currentOrganization?.id]);

    // Initial fetch and window focus refresh - always active for badge
    useEffect(() => {
        fetchUnreadCount();

        const handleFocus = async () => {
            // If user is viewing a channel, sync last_read_at before fetching count
            if (joinedChannelRef.current && user?.id) {
                await markChannelRead(joinedChannelRef.current, user.id).catch(() => {});
            }
            fetchUnreadCount();
        };

        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [fetchUnreadCount]);

    // Sync OS-level app badge (Windows taskbar / macOS dock / Android launcher) with unread count.
    // Only supported in installed PWAs on browsers that implement the Badging API.
    useEffect(() => {
        const nav = navigator as Navigator & {
            setAppBadge?: (count?: number) => Promise<void>;
            clearAppBadge?: () => Promise<void>;
        };
        if (!nav.setAppBadge) return;
        if (totalUnreadCount > 0) {
            nav.setAppBadge(totalUnreadCount).catch(() => {});
        } else {
            nav.clearAppBadge?.().catch(() => {});
        }
    }, [totalUnreadCount]);

    // Reset UI-specific state when chat app is closed
    // Keep: WebSocket connection, totalUnreadCount, currentUserStatus (for badge + notifications)
    useEffect(() => {
        if (!isChatAppRunning) {
            setMessages({});
            setTypingUsers({});
            setOnlineUsers({});
            setChannelNotifications({});
            setChannelsNeedRefresh(false);
            setDeletedChannelIds(new Set());
            setThreadMessages({});
            setThreadTypingUsers({});
            setActiveThreadId(null);
            setPendingThreadUpdates([]);
            setPendingMessageDeletions([]);
            setPendingChannelUpdates([]);
            setPendingUserAvatarUpdates([]);
            setReactionUpdates([]);
            setMentionNotifications([]);
            setPendingMessageSendErrors(0);
            setUserStatuses(new Map());
            joinedChannelRef.current = null;
            if (pendingJoinRef.current) {
                clearTimeout(pendingJoinRef.current.timer);
                pendingJoinRef.current = null;
            }
        }
    }, [isChatAppRunning]);

    // WebSocket connection - always active when signed in (for badge + notifications)
    useEffect(() => {
        if (!user?.id) return;

        const socket = io(WEBSOCKET_URL, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 1000,        // Start at 1s
            reconnectionDelayMax: 30000,    // Cap at 30s
            randomizationFactor: 0.5,       // Jitter: ±50%
            reconnectionAttempts: Infinity,  // Never stop trying
        });

        socketRef.current = socket;

        socket.on('connect', () => {
            socket.emit('auth', {
                clerkUserId: user.id,
                organizationId: currentOrganization?.id
            });
        });

        socket.on('authenticated', () => {
            setConnected(true);
            // Clear stale last-message previews from before disconnect
            channelLastMessagesRef.current = {};
        });

        socket.on('disconnect', () => {
            setConnected(false);
        });

        // Listen for server errors (e.g., message save failed) — remove optimistic messages
        socket.on('error', (data: { message: string }) => {
            if (data?.message === 'Failed to save message' && messageDispatchRef.current) {
                // Signal the dispatch callback to clean up optimistic messages
                // We use a special sentinel message that the consumer recognizes
                setPendingMessageSendErrors(prev => prev + 1);
            }
        });

        socket.on('message:new', (message: Message) => {
            const isActiveChannel = message.channel_id === joinedChannelRef.current;

            // Update last message preview for sidebar BEFORE dispatch
            // (dispatch calls flushChannelLastMessages, so ref must be up-to-date)
            if (!message.thread_parent_id) {
                channelLastMessagesRef.current[message.channel_id] = {
                    content: message.content,
                    sender_name: message.user.name.split(' ')[0] || 'User',
                    created_at: message.created_at,
                };
            }

            // Direct dispatch for the active channel — bypasses setState → effect → reducer chain
            if (isActiveChannel && isChatAppRunningRef.current && messageDispatchRef.current && !message.thread_parent_id) {
                messageDispatchRef.current(message);
            } else if (isChatAppRunningRef.current) {
                // Fallback: accumulate in WS state for sync effect to pick up
                setMessages(prev => {
                    const channelId = message.channel_id;
                    const existing = prev[channelId] || [];
                    if (existing.some(m => m.id === message.id)) {
                        return prev;
                    }
                    const updated = [...existing, message];
                    return {
                        ...prev,
                        [channelId]: updated.length > 50 ? updated.slice(-50) : updated,
                    };
                });
            }

            // If message contains a meeting link, invalidate SoundStage queries so meeting appears for all users
            if (/\[MEETING:[a-zA-Z0-9-]+\]/.test(message.content)) {
                (window as any).__queryClient?.invalidateQueries?.({ queryKey: ['soundstage'] });
            }

            // Debounced mark-as-read for the active channel so server stays in sync
            // Only auto-mark when window is focused — if not, unread count should accumulate
            if (isActiveChannel && user?.id && document.hasFocus()) {
                if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current);
                markReadTimerRef.current = setTimeout(() => {
                    markChannelRead(message.channel_id, user.id!).catch(() => {});
                }, 3000);
            }
        });

        socket.on('message:notification', (data: MessageNotificationData) => {
            // Use document.hasFocus() — more reliable than document.hidden on Windows
            // document.hidden can stay false when window is minimized or behind another window
            const isActivelyViewing = document.hasFocus();

            // Skip unread/notification only when user is actively viewing the channel AND window is focused
            if (data.isInChannel && isActivelyViewing) return;

            // Only accumulate per-channel notifications when chat app is open.
            // When it's closed, totalUnreadCount is incremented directly below AND
            // getChannels will return authoritative unread_count on next open —
            // accumulating here would double-count after the sync effect processes it.
            if (isChatAppRunningRef.current) {
                setChannelNotifications(prev => ({
                    ...prev,
                    [data.channelId]: (prev[data.channelId] || 0) + 1
                }));
            }

            // Update last message preview for sidebar (ref — no re-render)
            if (data.message && !data.message.thread_parent_id) {
                channelLastMessagesRef.current[data.channelId] = {
                    content: data.message.content,
                    sender_name: data.message.user.name.split(' ')[0] || 'User',
                    created_at: data.message.created_at,
                };
            }

            // When chat UI is closed, useChatSync doesn't run — increment badge directly
            if (!isChatAppRunningRef.current) {
                setTotalUnreadCount(prev => prev + 1);
            }

            // Fire notification callback (desktop/sound — handled by consumer)
            if (data.message) {
                if (notificationCallbackRef.current) {
                    notificationCallbackRef.current({
                        type: 'message',
                        channelId: data.channelId,
                        senderName: data.message.user.name,
                        preview: data.message.content.substring(0, 120),
                        senderAvatar: data.message.user.avatar,
                    });
                } else {
                    // Chat window closed — fire notifications directly from prefs in localStorage
                    fireInlineNotification(
                        data.message.user.name,
                        data.message.content.substring(0, 120),
                        'message',
                        data.message.user.avatar,
                    );
                }
            }
        });

        socket.on('channel:deleted', (data: ChannelDeletedData) => {
            setDeletedChannelIds(prev => new Set(prev).add(data.channelId));
            setMessages(prev => {
                const newMessages = { ...prev };
                delete newMessages[data.channelId];
                return newMessages;
            });
            setChannelNotifications(prev => {
                const newNotifications = { ...prev };
                delete newNotifications[data.channelId];
                return newNotifications;
            });
        });

        socket.on('channel:added', (data: ChannelAddedData) => {
            setChannelsNeedRefresh(true);
        });

        socket.on('channel:avatar_updated', (data: ChannelAvatarUpdatedData) => {
            setPendingChannelUpdates(prev => [...prev, data]);
        });

        socket.on('user:profile_updated', (data: UserProfileUpdatedData) => {
            setPendingProfileUpdates(prev => [...prev, data]);
        });

        socket.on('user:avatar_updated', (data: UserAvatarUpdatedData) => {
            setPendingUserAvatarUpdates(prev => [...prev, data]);
            // Also update avatar in WS message state for all channels in memory
            if (isChatAppRunningRef.current) {
                setMessages(prev => {
                    const updated: Record<string, Message[]> = {};
                    let changed = false;
                    for (const channelId in prev) {
                        const msgs = prev[channelId];
                        const newMsgs = msgs.map(msg =>
                            msg.user_profile_id === data.userProfileId
                                ? { ...msg, user: { ...msg.user, avatar: data.avatarUrl || undefined } }
                                : msg
                        );
                        updated[channelId] = newMsgs;
                        if (newMsgs !== msgs) changed = true;
                    }
                    return changed ? updated : prev;
                });
            }
        });

        socket.on('message:updated', (data: MessageUpdatedData) => {
            setMessages(prev => ({
                ...prev,
                [data.channel_id]: prev[data.channel_id]?.map(msg =>
                    msg.id === data.id ? { ...msg, content: data.content, is_edited: true } : msg
                ) || []
            }));
            setPendingMessageEdits(prev => {
                const next = [...prev, { id: data.id, content: data.content }];
                return next.length > 500 ? next.slice(-200) : next;
            });
        });

        socket.on('message:deleted', (data: MessageDeletedData) => {
            setMessages(prev => ({
                ...prev,
                [data.channelId]: prev[data.channelId]?.filter(msg => msg.id !== data.messageId) || []
            }));
            setPendingMessageDeletions(prev => {
                const next = [...prev, data.messageId];
                return next.length > 500 ? next.slice(-200) : next;
            });
        });

        socket.on('message:hidden', (data: MessageDeletedData) => {
            if (data.channelId) {
                setMessages(prev => ({
                    ...prev,
                    [data.channelId]: prev[data.channelId]?.filter(msg => msg.id !== data.messageId) || []
                }));
            }
            setPendingMessageDeletions(prev => {
                const next = [...prev, data.messageId];
                return next.length > 500 ? next.slice(-200) : next;
            });
        });

        // Typing handlers
        const clientTypingTimeouts = new Map<string, NodeJS.Timeout>();

        socket.on('typing:active', (data: TypingActiveData) => {
            const timeoutKey = `${data.channelId}:${data.user.id}`;

            if (clientTypingTimeouts.has(timeoutKey)) {
                clearTimeout(clientTypingTimeouts.get(timeoutKey)!);
            }

            setTypingUsers(prev => {
                const existing = prev[data.channelId] || [];
                if (existing.some(u => u.id === data.user.id)) {
                    return prev;
                }
                return {
                    ...prev,
                    [data.channelId]: [...existing, data.user]
                };
            });

            const timeout = setTimeout(() => {
                setTypingUsers(prev => ({
                    ...prev,
                    [data.channelId]: prev[data.channelId]?.filter(u => u.id !== data.user.id) || []
                }));
                clientTypingTimeouts.delete(timeoutKey);
            }, 3000);

            clientTypingTimeouts.set(timeoutKey, timeout);
        });

        socket.on('typing:inactive', (data: TypingStoppedData) => {
            setTypingUsers(prev => ({
                ...prev,
                [data.channelId]: prev[data.channelId]?.filter(u => u.id !== data.userId) || []
            }));
        });

        socket.on('typing:stopped', (data: TypingStoppedData) => {
            setTypingUsers(prev => ({
                ...prev,
                [data.channelId]: prev[data.channelId]?.filter(u => u.id !== data.userId) || []
            }));
        });

        socket.on('channel:joined', (data: ChannelJoinedData) => {
            joinedChannelRef.current = data.channelId;
            // Clear retry timer on successful join
            if (pendingJoinRef.current?.channelId === data.channelId) {
                clearTimeout(pendingJoinRef.current.timer);
                pendingJoinRef.current = null;
            }
            setOnlineUsers(prev => ({
                ...prev,
                [data.channelId]: data.onlineMembers || []
            }));
        });

        socket.on('channel:user_joined', (data: ChannelUserData) => {
            setOnlineUsers(prev => {
                const existing = prev[data.channelId] || [];
                // Deduplicate — reconnection can send duplicate join events
                if (existing.some(u => u.id === data.user.id)) return prev;
                return { ...prev, [data.channelId]: [...existing, data.user] };
            });
        });

        socket.on('channel:user_left', (data: ChannelUserData) => {
            setOnlineUsers(prev => ({
                ...prev,
                [data.channelId]: prev[data.channelId]?.filter(u => u.id !== data.user.id) || []
            }));
        });

        // Thread event handlers
        socket.on('thread:joined', () => {
            // Thread joined confirmation
        });

        socket.on('thread:message:new', (message: Message) => {
            const threadId = message.thread_parent_id;
            if (!threadId) return;

            setThreadMessages(prev => {
                const existing = prev[threadId] || [];
                if (existing.some(m => m.id === message.id)) {
                    return prev;
                }
                return {
                    ...prev,
                    [threadId]: [...existing, message]
                };
            });
        });

        socket.on('thread:updated', (data: ThreadUpdate) => {
            // Only use pendingThreadUpdates path — useChatSync will dispatch
            // UPDATE_THREAD_STATS to update the reducer's messages directly.
            // No need to modify WS context messages (it caused unnecessary re-processing).
            setPendingThreadUpdates(prev => [...prev, data]);
        });

        // Thread typing handlers
        const clientThreadTypingTimeouts = new Map<string, NodeJS.Timeout>();

        socket.on('thread:typing:active', (data: { threadParentId: string; user: TypingUser }) => {
            const timeoutKey = `${data.threadParentId}:${data.user.id}`;

            if (clientThreadTypingTimeouts.has(timeoutKey)) {
                clearTimeout(clientThreadTypingTimeouts.get(timeoutKey)!);
            }

            setThreadTypingUsers(prev => {
                const existing = prev[data.threadParentId] || [];
                if (existing.some(u => u.id === data.user.id)) {
                    return prev;
                }
                return {
                    ...prev,
                    [data.threadParentId]: [...existing, data.user]
                };
            });

            const timeout = setTimeout(() => {
                setThreadTypingUsers(prev => ({
                    ...prev,
                    [data.threadParentId]: prev[data.threadParentId]?.filter(u => u.id !== data.user.id) || []
                }));
                clientThreadTypingTimeouts.delete(timeoutKey);
            }, 3000);

            clientThreadTypingTimeouts.set(timeoutKey, timeout);
        });

        socket.on('thread:typing:stopped', (data: { threadParentId: string; userId: string }) => {
            setThreadTypingUsers(prev => ({
                ...prev,
                [data.threadParentId]: prev[data.threadParentId]?.filter(u => u.id !== data.userId) || []
            }));
        });

        // Mention event handlers
        // Note: Don't increment channelNotifications here - message:notification already handles unread count.
        // This handler only tracks mention-specific notifications (for the @ badge).
        socket.on('mention:new', (data: MentionNewEvent) => {
            setMentionNotifications(prev => {
                const next = [...prev, data];
                return next.length > 200 ? next.slice(-100) : next;
            });

            // Fire mention-specific notification callback
            if (notificationCallbackRef.current) {
                notificationCallbackRef.current({
                    type: 'mention',
                    channelId: data.channelId,
                    senderName: data.mentionedBy.name,
                    preview: data.preview,
                    senderAvatar: data.mentionedBy.avatar,
                });
            } else {
                // Chat window closed — fire notifications directly
                fireInlineNotification(data.mentionedBy.name, data.preview, 'mention', data.mentionedBy.avatar);
            }
        });

        // Reaction event handlers (capped to prevent unbounded memory growth)
        socket.on('reaction:updated', (data: ReactionUpdate) => {
            setReactionUpdates(prev => {
                const next = [...prev, data];
                return next.length > 200 ? next.slice(-100) : next;
            });
        });

        // Presence event handlers
        socket.on('presence:online_users', (data: { userProfileIds: string[]; statusMap?: Record<string, UserStatus> }) => {
            const map = new Map<string, UserStatus>();
            if (data.statusMap) {
                for (const [id, status] of Object.entries(data.statusMap)) {
                    map.set(id, status);
                }
            } else {
                // Fallback for older servers without statusMap
                for (const id of data.userProfileIds) {
                    map.set(id, 'online');
                }
            }
            setUserStatuses(map);
        });
        socket.on('presence:user_online', (data: { userProfileId: string; status?: UserStatus }) => {
            setUserStatuses(prev => {
                const next = new Map(prev);
                next.set(data.userProfileId, data.status || 'online');
                return next;
            });
        });
        socket.on('presence:user_offline', (data: { userProfileId: string }) => {
            setUserStatuses(prev => {
                const next = new Map(prev);
                next.delete(data.userProfileId);
                return next;
            });
        });
        socket.on('presence:user_status_changed', (data: { userProfileId: string; status: UserStatus }) => {
            setUserStatuses(prev => {
                const next = new Map(prev);
                next.set(data.userProfileId, data.status);
                return next;
            });
        });

        return () => {
            // Clear pending typing timeouts to prevent stale callbacks
            for (const timeout of clientTypingTimeouts.values()) {
                clearTimeout(timeout);
            }
            clientTypingTimeouts.clear();
            for (const timeout of clientThreadTypingTimeouts.values()) {
                clearTimeout(timeout);
            }
            clientThreadTypingTimeouts.clear();
            socket.disconnect();
        };
    }, [user?.id, currentOrganization?.id]);

    // Channel methods
    const joinChannel = useCallback((channelId: string) => {
        if (!socketRef.current || !connected) return;

        // Clear any previous retry timer
        if (pendingJoinRef.current) {
            clearTimeout(pendingJoinRef.current.timer);
            pendingJoinRef.current = null;
        }

        socketRef.current.emit('channel:join', { channelId });

        // Set up retry: if channel:joined confirmation doesn't arrive within 3s, retry
        const timer = setTimeout(() => {
            if (joinedChannelRef.current !== channelId && socketRef.current?.connected) {
                socketRef.current.emit('channel:join', { channelId });
            }
            pendingJoinRef.current = null;
        }, 3000);

        pendingJoinRef.current = { channelId, timer };
    }, [connected]);

    const leaveChannel = useCallback((channelId: string) => {
        if (!socketRef.current) return;
        // Clear retry timer and joined state for this channel
        if (pendingJoinRef.current?.channelId === channelId) {
            clearTimeout(pendingJoinRef.current.timer);
            pendingJoinRef.current = null;
        }
        if (joinedChannelRef.current === channelId) {
            joinedChannelRef.current = null;
        }
        socketRef.current.emit('channel:leave', { channelId });
    }, []);

    const deleteChannel = useCallback((channelId: string) => {
        if (!socketRef.current || !connected) return;
        socketRef.current.emit('channel:delete', { channelId });
    }, [connected]);

    const notifyMemberAdded = useCallback((channelId: string, userProfileId: string) => {
        if (!socketRef.current || !connected) return;
        socketRef.current.emit('channel:member_added', { channelId, userProfileId });
    }, [connected]);

    const clearChannelsRefresh = useCallback(() => {
        setChannelsNeedRefresh(false);
    }, []);

    const clearDeletedChannel = useCallback((channelId: string) => {
        setDeletedChannelIds(prev => {
            const next = new Set(prev);
            next.delete(channelId);
            return next;
        });
    }, []);

    const notifyChannelAvatarUpdate = useCallback((channelId: string, avatarUrl: string | null) => {
        if (!socketRef.current || !connected) return;
        socketRef.current.emit('channel:update_avatar', { channelId, avatarUrl });
    }, [connected]);

    const notifyUserAvatarUpdate = useCallback((avatarUrl: string | null) => {
        if (!socketRef.current || !connected) return;
        socketRef.current.emit('user:update_avatar', { avatarUrl });
    }, [connected]);

    const notifyUserProfileUpdate = useCallback((updates: { bannerColor?: string | null; bannerUrl?: string | null; bio?: string | null }) => {
        if (!socketRef.current || !connected) return;
        socketRef.current.emit('user:update_profile', updates);
    }, [connected]);

    const clearChannelNotifications = useCallback((channelId: string) => {
        setChannelNotifications(prev => {
            const next = { ...prev };
            delete next[channelId];
            return next;
        });
    }, []);

    const flushChannelLastMessages = useCallback((): Record<string, LastMessagePreview> => {
        const pending = channelLastMessagesRef.current;
        if (Object.keys(pending).length === 0) return pending;
        channelLastMessagesRef.current = {};
        return pending;
    }, []);

    const adjustTotalUnread = useCallback((delta: number) => {
        setTotalUnreadCount(prev => Math.max(0, prev + delta));
    }, []);

    // Message methods
    const sendMessage = useCallback((channelId: string, content: string, replyToId?: string, attachments?: ChatAttachment[]) => {
        if (!socketRef.current || !connected) return;
        socketRef.current.emit('message:send', {
            channelId,
            content,
            replyToMessageId: replyToId,
            attachments: attachments || []
        });
    }, [connected]);

    const editMessage = useCallback((messageId: string, content: string) => {
        if (!socketRef.current || !connected) return;
        socketRef.current.emit('message:edit', { messageId, content });
    }, [connected]);

    const deleteMessage = useCallback((messageId: string, mode: 'for_me' | 'for_everyone' = 'for_everyone') => {
        if (!socketRef.current || !connected) return;
        socketRef.current.emit('message:delete', { messageId, mode });
    }, [connected]);

    // Typing methods
    const typingTimeoutRef = useRef<Record<string, NodeJS.Timeout>>({});

    const startTyping = useCallback((channelId: string) => {
        if (!socketRef.current || !connected) return;

        if (typingTimeoutRef.current[channelId]) {
            clearTimeout(typingTimeoutRef.current[channelId]);
        }

        socketRef.current.emit('typing:start', { channelId });

        typingTimeoutRef.current[channelId] = setTimeout(() => {
            socketRef.current?.emit('typing:stop', { channelId });
        }, 3000);
    }, [connected]);

    const stopTyping = useCallback((channelId: string) => {
        if (!socketRef.current || !connected) return;

        if (typingTimeoutRef.current[channelId]) {
            clearTimeout(typingTimeoutRef.current[channelId]);
            delete typingTimeoutRef.current[channelId];
        }

        socketRef.current.emit('typing:stop', { channelId });
    }, [connected]);

    // Thread methods
    const joinThread = useCallback((threadParentId: string, channelId: string) => {
        if (!socketRef.current || !connected) return;
        socketRef.current.emit('thread:join', { threadParentId, channelId });
        setActiveThreadId(threadParentId);
    }, [connected]);

    const leaveThread = useCallback((threadParentId: string) => {
        if (!socketRef.current) return;
        socketRef.current.emit('thread:leave', { threadParentId });
        setActiveThreadId(null);
    }, []);

    const sendThreadMessage = useCallback((
        channelId: string,
        threadParentId: string,
        content: string,
        replyToId?: string,
        attachments?: ChatAttachment[]
    ) => {
        if (!socketRef.current || !connected) return;
        socketRef.current.emit('message:send', {
            channelId,
            content,
            threadParentId,
            replyToMessageId: replyToId,
            attachments: attachments || []
        });
    }, [connected]);

    const threadTypingTimeoutRef = useRef<Record<string, NodeJS.Timeout>>({});

    const startThreadTyping = useCallback((threadParentId: string) => {
        if (!socketRef.current || !connected) return;

        if (threadTypingTimeoutRef.current[threadParentId]) {
            clearTimeout(threadTypingTimeoutRef.current[threadParentId]);
        }

        socketRef.current.emit('thread:typing:start', { threadParentId });

        threadTypingTimeoutRef.current[threadParentId] = setTimeout(() => {
            socketRef.current?.emit('thread:typing:stop', { threadParentId });
        }, 3000);
    }, [connected]);

    const stopThreadTyping = useCallback((threadParentId: string) => {
        if (!socketRef.current || !connected) return;

        if (threadTypingTimeoutRef.current[threadParentId]) {
            clearTimeout(threadTypingTimeoutRef.current[threadParentId]);
            delete threadTypingTimeoutRef.current[threadParentId];
        }

        socketRef.current.emit('thread:typing:stop', { threadParentId });
    }, [connected]);

    const clearThreadMessages = useCallback((threadParentId: string) => {
        setThreadMessages(prev => {
            const next = { ...prev };
            delete next[threadParentId];
            return next;
        });
    }, []);

    const clearPendingThreadUpdates = useCallback(() => {
        setPendingThreadUpdates([]);
    }, []);

    const clearPendingMessageDeletions = useCallback(() => {
        setPendingMessageDeletions([]);
    }, []);

    const clearPendingMessageEdits = useCallback(() => {
        setPendingMessageEdits([]);
    }, []);

    const clearPendingChannelUpdates = useCallback(() => {
        setPendingChannelUpdates([]);
    }, []);

    const clearPendingUserAvatarUpdates = useCallback(() => {
        setPendingUserAvatarUpdates([]);
    }, []);

    const clearPendingProfileUpdates = useCallback(() => {
        setPendingProfileUpdates([]);
    }, []);

    const clearPendingMessageSendErrors = useCallback(() => {
        setPendingMessageSendErrors(0);
    }, []);

    // Atomically read + clear WS messages for a channel (synchronous via ref)
    const consumeWsMessages = useCallback((channelId: string): Message[] => {
        const msgs = wsMessagesRef.current[channelId] || [];
        if (msgs.length > 0) {
            setMessages(prev => {
                const next = { ...prev };
                delete next[channelId];
                return next;
            });
        }
        return msgs;
    }, [setMessages]);

    const clearConsumedWsMessages = useCallback((channelId: string) => {
        setMessages(prev => {
            if (!prev[channelId] || prev[channelId].length === 0) return prev;
            const next = { ...prev };
            delete next[channelId];
            return next;
        });
    }, []);

    // Reaction methods
    const toggleReaction = useCallback((channelId: string, messageId: string, emoji: string) => {
        if (!socketRef.current || !connected) return;
        socketRef.current.emit('reaction:toggle', { channelId, messageId, emoji });
    }, [connected]);

    const forwardMessage = useCallback((targetChannelId: string, originalMessageId: string, content: string, attachments?: any[]) => {
        if (!socketRef.current || !connected) return;
        socketRef.current.emit('message:send', {
            channelId: targetChannelId,
            content,
            attachments: attachments && attachments.length > 0 ? attachments : undefined,
            forwardedFrom: { originalMessageId }
        });
    }, [connected]);

    const clearReactionUpdates = useCallback(() => {
        setReactionUpdates([]);
    }, []);

    // Mention methods
    const clearMentionNotifications = useCallback(() => {
        setMentionNotifications([]);
    }, []);

    // Notification callback registration
    const registerNotificationCallback = useCallback((cb: (event: NotificationEvent) => void) => {
        notificationCallbackRef.current = cb;
        return () => { notificationCallbackRef.current = null; };
    }, []);

    // Direct message dispatch callback registration (bypasses WS state → sync effect chain)
    const registerMessageDispatch = useCallback((cb: (message: Message) => void) => {
        messageDispatchRef.current = cb;
        return () => { messageDispatchRef.current = null; };
    }, []);

    // Status method
    const setUserStatus = useCallback((status: UserStatus) => {
        setCurrentUserStatus(status);
        if (socketRef.current?.connected) {
            socketRef.current.emit('presence:set_status', { status });
        }
    }, []);

    // Computed display count
    const unreadDisplayCount = useMemo(() => {
        return totalUnreadCount > 99 ? '99+' : String(totalUnreadCount);
    }, [totalUnreadCount]);

    // ==================== SUB-CONTEXT VALUES ====================
    // Each is independently memoized so changes in one don't trigger re-renders in others

    const socketValue = useMemo<ChatSocketContextType>(() => ({
        connected,
        joinChannel, leaveChannel, deleteChannel, notifyMemberAdded, notifyChannelAvatarUpdate, notifyUserAvatarUpdate,
        sendMessage, editMessage, deleteMessage,
        startTyping, stopTyping,
        joinThread, leaveThread, sendThreadMessage, startThreadTyping, stopThreadTyping,
        toggleReaction, forwardMessage,
        clearChannelNotifications, flushChannelLastMessages, clearChannelsRefresh, clearDeletedChannel,
        clearThreadMessages, clearPendingThreadUpdates, clearPendingMessageDeletions, clearPendingMessageEdits, clearReactionUpdates, clearMentionNotifications,
        clearPendingChannelUpdates, clearPendingUserAvatarUpdates, clearPendingProfileUpdates,
        clearConsumedWsMessages,
        consumeWsMessages,
        clearPendingMessageSendErrors,
        notifyUserProfileUpdate,
        registerNotificationCallback, registerMessageDispatch,
    }), [
        connected,
        joinChannel, leaveChannel, deleteChannel, notifyMemberAdded, notifyChannelAvatarUpdate, notifyUserAvatarUpdate,
        sendMessage, editMessage, deleteMessage,
        startTyping, stopTyping,
        joinThread, leaveThread, sendThreadMessage, startThreadTyping, stopThreadTyping,
        toggleReaction, forwardMessage,
        clearChannelNotifications, flushChannelLastMessages, clearChannelsRefresh, clearDeletedChannel,
        clearThreadMessages, clearPendingThreadUpdates, clearPendingMessageDeletions, clearPendingMessageEdits, clearReactionUpdates, clearMentionNotifications,
        clearPendingChannelUpdates, clearPendingUserAvatarUpdates, clearPendingProfileUpdates,
        clearConsumedWsMessages, consumeWsMessages, clearPendingMessageSendErrors,
        notifyUserProfileUpdate,
        registerNotificationCallback, registerMessageDispatch,
    ]);

    const messagesValue = useMemo<ChatMessagesContextType>(() => ({
        messages, threadMessages,
        channelNotifications, channelsNeedRefresh, deletedChannelIds,
        activeThreadId, pendingThreadUpdates, pendingMessageDeletions, pendingMessageEdits, pendingChannelUpdates, pendingUserAvatarUpdates, pendingProfileUpdates,
        pendingMessageSendErrors, reactionUpdates, mentionNotifications,
    }), [
        messages, threadMessages,
        channelNotifications, channelsNeedRefresh, deletedChannelIds,
        activeThreadId, pendingThreadUpdates, pendingMessageDeletions, pendingMessageEdits, pendingChannelUpdates, pendingUserAvatarUpdates, pendingProfileUpdates,
        pendingMessageSendErrors, reactionUpdates, mentionNotifications,
    ]);

    const presenceValue = useMemo<ChatPresenceContextType>(() => ({
        typingUsers, onlineUsers, threadTypingUsers, onlineUserIds,
        userStatuses, currentUserStatus, setUserStatus,
    }), [typingUsers, onlineUsers, threadTypingUsers, onlineUserIds, userStatuses, currentUserStatus, setUserStatus]);

    const unreadValue = useMemo<ChatUnreadContextType>(() => ({
        totalUnreadCount, unreadDisplayCount,
        refreshUnreadCount: fetchUnreadCount,
        adjustTotalUnread,
    }), [totalUnreadCount, unreadDisplayCount, fetchUnreadCount, adjustTotalUnread]);

    return (
        <ChatSocketContext.Provider value={socketValue}>
            <ChatMessagesContext.Provider value={messagesValue}>
                <ChatPresenceContext.Provider value={presenceValue}>
                    <ChatUnreadContext.Provider value={unreadValue}>
                        {children}
                    </ChatUnreadContext.Provider>
                </ChatPresenceContext.Provider>
            </ChatMessagesContext.Provider>
        </ChatSocketContext.Provider>
    );
}

// ==================== SELECTIVE HOOKS ====================
// Use these in components that only need a subset of the state
// to avoid re-renders from unrelated state changes.

export function useChatSocket() {
    const ctx = useContext(ChatSocketContext);
    if (!ctx) throw new Error('useChatSocket must be used within ChatWebSocketProvider');
    return ctx;
}

export function useChatMessages() {
    const ctx = useContext(ChatMessagesContext);
    if (!ctx) throw new Error('useChatMessages must be used within ChatWebSocketProvider');
    return ctx;
}

export function useChatPresence() {
    const ctx = useContext(ChatPresenceContext);
    if (!ctx) throw new Error('useChatPresence must be used within ChatWebSocketProvider');
    return ctx;
}

export function useChatUnread() {
    const ctx = useContext(ChatUnreadContext);
    if (!ctx) throw new Error('useChatUnread must be used within ChatWebSocketProvider');
    return ctx;
}

// ==================== BACKWARDS-COMPATIBLE HOOKS ====================
// Combined hook — subscribes to ALL sub-contexts (same API as before).
// Prefer the selective hooks above for better performance.

export function useChatWebSocket(): ChatWebSocketContextType {
    const socket = useContext(ChatSocketContext);
    const messages = useContext(ChatMessagesContext);
    const presence = useContext(ChatPresenceContext);
    const unread = useContext(ChatUnreadContext);
    if (!socket || !messages || !presence || !unread) {
        throw new Error('useChatWebSocket must be used within ChatWebSocketProvider');
    }
    return { ...socket, ...messages, ...presence, ...unread };
}

export function useChatUnreadCount() {
    const ctx = useContext(ChatUnreadContext);
    const presence = useContext(ChatPresenceContext);
    if (!ctx) {
        throw new Error('useChatUnreadCount must be used within ChatWebSocketProvider');
    }
    // DND badge suppression: hide dock badge count when in DND mode
    const isDnd = presence?.currentUserStatus === 'dnd';
    return {
        totalUnread: isDnd ? 0 : ctx.totalUnreadCount,
        displayCount: isDnd ? '0' : ctx.unreadDisplayCount,
        isLoading: false,
        refresh: ctx.refreshUnreadCount,
    };
}
